import { IPost } from '_interfaces/post.interface';
import { IPostType } from '_interfaces/systemPost.types';
import React, { memo } from 'react';
import { getSystemPostNavigation, NavigationHandlers } from 'utils/systemPostHelpers';
import { FileCommentPost, ScheduleItemPost, SelectionItemPost } from './components';

interface SystemPostItemProps {
  post: IPost;
  navigationHandlers?: NavigationHandlers;
}

/**
 * SystemPostItem - Router component that renders the appropriate system post type
 *
 * This component delegates to specific post type components based on the post type.
 * Each post type has its own unique design and layout.
 *
 * @example
 * ```tsx
 * <SystemPostItem
 *   post={post}
 *   navigationHandlers={{
 *     navigateToDocument: (fileId, docId, commentId) => {
 *       navigate(`/files/${fileId}?commentId=${commentId}`);
 *     },
 *     navigateToSelection: (selectionId, itemId) => {
 *       navigate(`/selections/${selectionId}/items/${itemId}`);
 *     },
 *     navigateToSchedule: (scheduleId, itemId) => {
 *       navigate(`/schedules/${scheduleId}/items/${itemId}`);
 *     },
 *   }}
 * />
 * ```
 */
export const SystemPostItem: React.FC<SystemPostItemProps> = memo(
  ({ post, navigationHandlers = {} }) => {
    if (!post.isSystemPost() || !post.systemMetadata) {
      return null;
    }

    const handleClick = getSystemPostNavigation(post, navigationHandlers);

    switch (post.postType) {
      case IPostType.FILE_COMMENT:
        return <FileCommentPost post={post} onClick={handleClick} />;

      case IPostType.SELECTION_ITEM_POST:
        return <SelectionItemPost post={post} onClick={handleClick} />;

      case IPostType.SCHEDULE_ITEM_POST:
        return <ScheduleItemPost post={post} onClick={handleClick} />;

      default:
        return (
          <div>
            <p>Unknown system post type: {post.postType}</p>
          </div>
        );
    }
  }
);
